import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface LiveAnnouncerProps {
  message: string;
  priority?: 'polite' | 'assertive';
  clearDelay?: number;
}

export const LiveAnnouncer: React.FC<LiveAnnouncerProps> = ({
  message,
  priority = 'polite',
  clearDelay = 5000,
}) => {
  const [currentMessage, setCurrentMessage] = useState('');

  useEffect(() => {
    if (message) {
      setCurrentMessage(message);

      if (clearDelay > 0) {
        const timer = setTimeout(() => {
          setCurrentMessage('');
        }, clearDelay);

        return () => clearTimeout(timer);
      }
    }
  }, [message, clearDelay]);

  return (
    <Box
      role="status"
      aria-live={priority}
      aria-atomic="true"
      sx={{
        position: 'absolute',
        zIndex: Z_INDEX.toast,
        padding: 0,
        background: 'none',
        color: 'inherit',
        fontSize: '1px',
        border: 'none',
        clipPath: 'inset(100%)',
        visibility: currentMessage ? 'visible' : 'hidden',
      }}
    >
      {currentMessage}
    </Box>
  );
};

// Global live announcer hook
let announcerCallback: ((message: string, priority?: 'polite' | 'assertive') => void) | null = null;

export const setAnnouncerCallback = (callback: typeof announcerCallback) => {
  announcerCallback = callback;
};

export const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  if (announcerCallback) {
    announcerCallback(message, priority);
  }
};

// Global announcer component to be placed once in the app
export const GlobalLiveAnnouncer: React.FC = () => {
  const [announcement, setAnnouncement] = useState({ message: '', priority: 'polite' as 'polite' | 'assertive' });

  useEffect(() => {
    setAnnouncerCallback((message, priority = 'polite') => {
      setAnnouncement({ message, priority });
    });

    return () => {
      setAnnouncerCallback(null);
    };
  }, []);

  return <LiveAnnouncer message={announcement.message} priority={announcement.priority} />;
};

